import React, {PropsWithChildren, useContext} from 'react'
import {ProjectFolder} from './icons/ProjectFolder'
import styles from './styles.module.scss'
import {StudyPeriod} from './icons/StudyPeriod'
import {Cohorts} from './icons/Cohorts'
import {DescriptiveStats} from './icons/DescriptiveStats'
import {CohortAnalyser} from './icons/CohortAnalyser'
import {Link, LinkProps, match as Match, matchPath, useHistory, useLocation, useRouteMatch} from "react-router-dom";
import {buildStudyCohortRoute, buildStudyPeriodRoute, buildStudyStatsRoute, Routes} from "../../pages/routes";
import {useAppDispatch, useAppSelector} from "../../hooks";
import {markQueryRead, selectCurrent} from "../../containers/CohortBuilder/cohortBuilderSlice";
import {CohortDataset, Query} from "../../containers/HealthcareUsageReport/types/CohortQuery";
import { ReactComponent as CohortTrackerIcon } from '../../pages/homepage/icons/svg/Cohort-Tracker.svg';
import _ from "lodash";
import {Permission} from "../../containers/CohortBuilder/PermissionsUtil";
import {getPermissions, selectResources} from "../../actions/userSlice";
import {Tooltip} from "antd";
import {FINISHED_STATUS} from "../../containers/CohortBuilder/cohortStatus";
import {InfoCircleFilled} from '@ant-design/icons'
import {cohortAnalyserRoute, ProjectsPageContext, ProjectsPageContextType} from "../../pages/ProjectsPageContext";
import {getDatasetPermission} from "../cohort-queries/QueryCard";
import {findDataset} from "../../containers/CohortBuilder/DatasetPicker/DatasetPickerUtil";
import {datasets} from "../cohort-queries/ListQueries";
import ParentRouteRedirect from "../ParentRouteRedirect";

enum ItemState {
  Disabled,
  Active,
  Available,
  Forbidden,
}

type NavItemProps = {
  title: string,
  icon: () => JSX.Element
  route: string
  target?: LinkProps['target']
  state: ItemState
  forbiddenMessage?: string
  notification?: string
  onNavigate?: () => Promise<void>
}

const getStateClass = (state: ItemState) => {
  const useState = state === ItemState.Forbidden ? ItemState.Disabled : state
  return styles[ItemState[useState].toLowerCase()]
}

const studyPeriodConnectionAvailable = (path: string, query: Query | undefined) => {
   return !matchPath(path, {path: Routes.Projects}) && !query?.sparkQuery
}

const cohortConnectionAvailable = (path: string, query: Query | undefined): boolean => {
  const studyMatch = matchPath<{studyId: string}>(path, {path: Routes.Project})
  return !matchPath(path, {path: Routes.Projects}) && (!query?.sparkQuery && studyMatch?.params?.studyId !== undefined)
}

const statsConnectionAvailable = (path: string, query: Query | undefined): boolean => {
  return !matchPath(path, {path: Routes.Projects}) && query?.sparkQuery?.status?.toLowerCase() === FINISHED_STATUS
}

const studyPeriodState = (projectMatch: Match, studyMatch: Match<{subpage: string}>, query: Query | undefined) => {
  if (projectMatch) {
    return ItemState.Disabled
  }

  if (studyMatch.params?.subpage === 'period') {
    return ItemState.Active
  }

  return _.isNil(query?.sparkQuery) ? ItemState.Available : ItemState.Disabled
}

const cohortState = (
    projectMatch: Match,
    studyMatch: Match<{projectId: string, studyId: string, subpage: string}>,
) => {
  if (projectMatch) {
    return ItemState.Disabled
  }

  if (studyMatch.params?.subpage === undefined) {
    return ItemState.Active
  }

  return studyMatch.params.studyId === undefined ? ItemState.Disabled : ItemState.Available
}

const statsState = (
    projectMatch: Match,
    studyMatch: Match<{projectId: string, studyId: string, subpage: string}>,
    query: Query | undefined,
    hasDatasetPermission: boolean
) => {
  if (projectMatch) {
    return ItemState.Disabled
  }

  if (!hasDatasetPermission) {
    return ItemState.Forbidden
  }

  if (studyMatch.params?.subpage === 'stats') {
    return ItemState.Active
  }

  return query?.sparkQuery?.status?.toLowerCase() === FINISHED_STATUS ? ItemState.Available : ItemState.Disabled
}

const cohortAnalyserState = (
    projectMatch: Match,
    studyMatch: Match<{projectId: string, studyId: string, subpage: string}>,
    query: Query | undefined,
    permissions: Permission,
    hasDatasetPermission: boolean
) => {
  if (projectMatch) {
    return ItemState.Disabled
  }

  if (!permissions.qb || !permissions.cb || !hasDatasetPermission) {
    return ItemState.Forbidden
  }

  if (!query?.trackable) {
    return ItemState.Disabled
  }

  return query?.sparkQuery?.status?.toLowerCase() === FINISHED_STATUS ? ItemState.Available : ItemState.Disabled
}

const Connection = ({
  connectionIndex,
  highlight
}: {
  connectionIndex: number
  highlight: boolean
}) => {
  return (
      <div
          className={`${styles[`connection-${connectionIndex}`]} ${getStateClass(
              highlight ? ItemState.Available : ItemState.Disabled
          )}`}
      />
  )
}

type OptionalLinkProps = PropsWithChildren<{
  to: string,
  target?: LinkProps['target']
  className: string,
  disabled?: boolean
  onMouseEnter?: React.DOMAttributes<HTMLElement>['onMouseEnter']
  onMouseLeave?: React.DOMAttributes<HTMLElement>['onMouseEnter']
  onFocus?: React.DOMAttributes<HTMLElement>['onFocus']
  onClick?: React.DOMAttributes<HTMLElement>['onClick']
  onNavigate?: () => Promise<void>
}>

const OptionalLink = ({
  to,
  target,
  className,
  disabled,
  children,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onFocus,
  onNavigate
}: OptionalLinkProps) => {
  const forwardProps = {onMouseEnter, onMouseLeave, onFocus}
  const matchStudyPeriod = useRouteMatch(Routes.Project)?.params?.subpage === 'period'
  const toMatchesCohort = matchPath<{subpage: string}>(to.toString(), {path: Routes.Project})
  const destinationIsCohortPage = toMatchesCohort && toMatchesCohort.params?.subpage === undefined
  const state = matchStudyPeriod && destinationIsCohortPage ? {fromStudyPeriod: true} : {}
  const history = useHistory()

  return disabled
      ? <span
          className={className}
          {...forwardProps}
      >
        {children}
        </span>
      : <Link
          target={target}
          to={{state, pathname: to}}
          className={className}
          {...forwardProps}
          onClick={onNavigate === undefined ? onClick : (e) => {
            e.preventDefault()
            onNavigate()
                .then(() => {
                  if (target === "_blank") {
                    window.open(to, '_blank');
                  } else {
                    history.push({state, pathname: to})
                  }
                })
          }}
      >
        {children}
      </Link>
}

const Notification = ({notification}: { notification: string | undefined }) => {
  if (notification === undefined) {
    return null;
  }

  return <div className={styles['unread-notification']}>
    <InfoCircleFilled/>
    {notification}
  </div>
}

const NavItem = ({ title, icon, route, state, target, forbiddenMessage, notification, onNavigate }: NavItemProps) => {
  const Icon = icon
  const stateClass = getStateClass(state)

  return (
      <>
      <Tooltip title={ state === ItemState.Forbidden ? forbiddenMessage : undefined}>
      <OptionalLink
          disabled={state === ItemState.Disabled || state === ItemState.Forbidden}
          to={route}
          target={target}
          className={`${styles['nav-item']} ${stateClass}`}
          onNavigate={onNavigate}
      >
        <div
            className={`${styles['icon-background']} ${stateClass}`}
            data-testid={`nav-${title.toLowerCase().replaceAll(' ', '-')}`}
        >
          <div className={`${styles.icon} ${stateClass}`}>
            <Icon/>
          </div>
        </div>
        <span>{title}</span>
        {state === ItemState.Active ? (
            <div className={styles['active-notch']} />
        ) : null}
      </OptionalLink>
      </Tooltip>
      <Notification notification={notification}/>
      </>
  )
}

type CohortNavigationProps = {
  showCohortAnalyserSwitch: boolean
}

export const CohortNavigation = ({showCohortAnalyserSwitch}: CohortNavigationProps) => {
  const path = useLocation().pathname
  const  { query } = useAppSelector(selectCurrent)
  const projectsMatch = useRouteMatch<{projectId: number, studyId: number}>(Routes.Projects)
  const studyMatch = useRouteMatch<{projectId: number, studyId: number}>(Routes.Project)
  const permissions = useAppSelector(getPermissions)
  const {projectId, studyId} = studyMatch?.params || {}
  const dispatch = useAppDispatch()
  const resources = useAppSelector(selectResources)
  const permittedDatasets = datasets(resources)
  let hasDatasetPermission = false

  if (query) {
    const dataset = findDataset(query.dataset)!
    hasDatasetPermission = getDatasetPermission(dataset.key as CohortDataset, permittedDatasets)
  }

  const {
    classic,
  } = useContext(ProjectsPageContext) as ProjectsPageContextType

  return (
    <>
      <div className={styles['support-bar']}>
        <ParentRouteRedirect route={'/projects'}>
          <span>
              <CohortTrackerIcon className={styles['tracker-icon']} />
              <h2 className={styles['title']}>Cohort Builder</h2>
          </span>
        </ParentRouteRedirect>
        <div className={styles.controls}>
          <Connection connectionIndex={0} highlight={studyPeriodConnectionAvailable(path, query)} />
          <Connection connectionIndex={1} highlight={cohortConnectionAvailable(path, query)} />
          <Connection connectionIndex={2} highlight={statsConnectionAvailable(path, query)} />
          <Connection connectionIndex={3} highlight={statsConnectionAvailable(path, query)} />
          <NavItem
              title='Projects'
              icon={ProjectFolder}
              route={Routes.Projects}
              state={matchPath(path, {path: Routes.Projects}) ? ItemState.Active : ItemState.Available}
          />
          <NavItem
              title={'Study Period'}
              icon={StudyPeriod}
              route={projectId === undefined ? '' : buildStudyPeriodRoute(projectId, studyId)}
              state={studyPeriodState(projectsMatch, studyMatch, query)}
          />
          <NavItem
              title={'Cohort'}
              icon={Cohorts}
              route={projectId === undefined || studyId === undefined ? '' : buildStudyCohortRoute(projectId, studyId)}
              state={cohortState(projectsMatch, studyMatch)}
          />
          <NavItem
              title={'Descriptive Stats'}
              icon={DescriptiveStats}
              route={(projectId === undefined || studyId === undefined) ? '' : buildStudyStatsRoute(projectId, studyId)}
              state={statsState(projectsMatch, studyMatch, query, hasDatasetPermission)}
              notification={hasDatasetPermission && studyId === query?.id?.toString() && query?.unread ?
                  "Your results are now ready. View them in Descriptive Stats or Cohort Analyzer."
                  : undefined
              }
          />

          <NavItem
              title={'Cohort Analyzer'}
              icon={CohortAnalyser}
              route={cohortAnalyserRoute(query, classic)}
              target="_blank"
              state={cohortAnalyserState(projectsMatch, studyMatch, query, permissions, hasDatasetPermission)}
              onNavigate={() => dispatch(markQueryRead(query?.id)).unwrap()}
              forbiddenMessage='You do not have access to Cohort Analyzer.'
          />
        </div>
      </div>
      <div className={styles['support-bar-underlay']}/>
    </>
  )
}
